/**
 * Leitura de números digitados no padrão brasileiro. A tela e a API usam a mesma função:
 * o usuário digita "10.000,00", "10000,00" ou "10000.00" e o sistema guarda "10000.00"
 * (texto com ponto, sem separador de milhar), que é o formato do banco (numeric).
 *
 * Regras, sem ambiguidade:
 *  - com vírgula: a vírgula é o separador decimal e os pontos só valem como milhar, em grupos
 *    de 3 ("1.234.567,89");
 *  - sem vírgula, em VALORES EM REAIS (modo "dinheiro"): "10.000" é dez mil (grupos de 3) e
 *    "10.50" é dez e cinquenta;
 *  - sem vírgula, nos demais números (quantidade, percentual, fator): o ponto é decimal, como
 *    sempre foi ("1.500" kg = 1,5 kg).
 */

export type DecimalMode = "dinheiro" | "numero";

const SPACES = /[\s\u00a0]/g;
const WITH_COMMA = /^(\d{1,3}(?:\.\d{3})+|\d+),(\d*)$/;
const MONEY_THOUSANDS = /^\d{1,3}(?:\.\d{3})+$/;
const PLAIN = /^\d+(?:\.\d+)?$/;

/**
 * Converte o texto digitado em decimal canônico ("1234.5"). Devolve null quando não é um
 * número válido. Não arredonda nem corta casas: quem chama confere o limite de casas.
 */
export function parseDecimalBR(
  input: string | number,
  mode: DecimalMode = "numero",
): string | null {
  if (typeof input === "number") {
    return Number.isFinite(input) && input >= 0 ? String(input) : null;
  }
  const s = input.replace(SPACES, "").replace(/^R\$/i, "");
  if (s === "") return null;
  const comma = WITH_COMMA.exec(s);
  if (comma) {
    const int = comma[1]!.replace(/\./g, "");
    const frac = comma[2]!;
    return frac === "" ? int : `${int}.${frac}`;
  }
  if (s.includes(",")) return null;
  if (mode === "dinheiro" && MONEY_THOUSANDS.test(s)) return s.replace(/\./g, "");
  return PLAIN.test(s) ? s : null;
}

/** Quantas casas decimais o texto canônico tem. */
export function decimalPlaces(canonical: string): number {
  const i = canonical.indexOf(".");
  return i < 0 ? 0 : canonical.length - i - 1;
}

/**
 * Valor para mostrar num campo, no padrão brasileiro: "10000.5" → "10.000,50" (com `places`
 * fixas) ou "1.5" → "1,5" (sem `places`). Texto que não é número volta como veio.
 */
export function formatDecimalBR(
  value: string | number | null | undefined,
  places?: number,
): string {
  if (value === null || value === undefined || value === "") return "";
  const canonical = parseDecimalBR(String(value), "numero");
  if (canonical === null) return String(value);
  const [int = "0", frac = ""] = canonical.split(".");
  const fixed =
    places === undefined ? frac : frac.padEnd(places, "0").slice(0, Math.max(places, 0));
  const grouped = int.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return fixed ? `${grouped},${fixed}` : grouped;
}

/** Valor em reais vindo da API ("10000.00") para um campo de formulário ("10.000,00"). */
export const toFormMoney = (value: string | number | null | undefined): string =>
  formatDecimalBR(value, 2);

/** Número vindo da API (quantidade, percentual, fator) para um campo ("1.250,5"). */
export const toFormNumber = (value: string | number | null | undefined): string =>
  formatDecimalBR(value);
